<template>
  <!-- Keyed: FontAwesome's <svg> is not a vnode, so a reused root would keep
       the other branch's icon. -->
  <div v-if="result.type === 'error'" key="error" class="scan-result scan-result--error" role="alert">
    <i class="fa-regular fa-circle-exclamation scan-result__error-icon" aria-hidden="true"></i>
    <div>
      <div class="scan-result__error-title">Could not sign you in or out</div>
      <div class="scan-result__error-message">{{ result.message }}</div>
    </div>
  </div>

  <div v-else key="done" class="scan-result" :class="`scan-result--${result.type}`">
    <span class="scan-result__check" aria-hidden="true">
      <i class="fa-solid fa-check"></i>
    </span>

    <span class="scan-result__chip">
      {{ chipLabel }}<template v-if="result.time"> · {{ result.time }}</template>
    </span>

    <div class="scan-result__name">{{ result.sessionName }}</div>

    <div v-if="isBackIn" class="scan-result__label scan-result__label--spaced">
      Earlier today {{ earlier }} — the timer picks up from there
    </div>

    <template v-if="result.type === 'sign-out' && result.totalMs != null">
      <div class="scan-result__total">{{ total }}</div>
      <div class="scan-result__label">
        <template v-if="hasEarlierVisits">Total time in the room · this visit {{ thisVisit }}</template>
        <template v-else>Time in the room</template>
      </div>
    </template>
  </div>
</template>

<script>
import { formatDuration } from './scan-clock';

export default {
  props: {
    // { type: 'sign-in' | 'sign-out' | 'error', sessionName, time, message,
    //   earlierMs (sign-in), totalMs + thisVisitMs (sign-out) }
    result: { type: Object, required: true },
  },
  computed: {
    isBackIn() {
      return this.result.type === 'sign-in' && this.result.earlierMs > 0;
    },

    hasEarlierVisits() {
      return this.result.thisVisitMs != null && this.result.thisVisitMs !== this.result.totalMs;
    },

    chipLabel() {
      if (this.result.type === 'sign-out') {
        return 'Signed out';
      }

      return this.isBackIn ? 'Signed back in' : 'Signed in';
    },

    total() {
      return formatDuration(this.result.totalMs);
    },

    earlier() {
      return formatDuration(this.result.earlierMs);
    },

    thisVisit() {
      return formatDuration(this.result.thisVisitMs);
    },
  },
}
</script>

<style lang="scss" scoped>
.scan-result {
  --tone: var(--scan-live);

  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 14px 16px;
  border: 1px solid color-mix(in srgb, var(--tone) 25%, transparent);
  border-radius: calc(var(--attendee-card-border-radius) + 4px);
  background-color: var(--attendee-card-color);
  color: var(--scan-ink);
  text-align: center;

  &--sign-out {
    --tone: var(--scan-brand);
  }

  &__check {
    display: grid;
    place-items: center;
    width: 62px;
    height: 62px;
    margin: 2px 0 12px;
    border-radius: 50%;
    background-color: var(--tone);
    box-shadow: 0 0 0 10px color-mix(in srgb, var(--tone) 16%, transparent);
    color: white;
    font-size: 28px;
  }

  &__chip {
    padding: 3px 10px;
    border-radius: 99px;
    background-color: color-mix(in srgb, var(--tone) 12%, transparent);
    color: var(--tone);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  &__name {
    margin-top: 8px;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  &__total {
    margin-top: 8px;
    color: var(--tone);
    font-size: 34px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__label {
    color: var(--scan-muted);
    font-size: 12px;

    &--spaced {
      margin-top: 6px;
    }
  }

  &--error {
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-color: color-mix(in srgb, var(--scan-danger) 28%, transparent);
    background-color: color-mix(in srgb, var(--scan-danger) 7%, var(--attendee-card-color));
    text-align: left;
  }

  &__error-icon {
    margin-top: 2px;
    color: var(--scan-danger);
    font-size: 20px;
  }

  &__error-title {
    color: var(--scan-danger);
    font-size: 15px;
    font-weight: 600;
  }

  &__error-message {
    margin-top: 4px;
    font-size: 14px;
    line-height: 1.5;
  }
}
</style>
